import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import type { UUID } from '@aline/common';
import { decodeUrlHashFragment, parseActivityAnchorId } from '@aline/common';
import { useLocation, useNavigate } from 'react-router-dom';
import { ARRIVAL_HIGHLIGHT_DURATION_MS } from 'src/editor/toolbars/right-panel/document-summary/use-arrival-highlight';

/** One request to navigate the activity feed, including repeat visits to the same destination. */
export interface ActivityNavigationRequest {
  /** Activity-item anchor supplied to the server. Legacy comment links do not have one. */
  activityItemId: UUID | null;
  /** Decoded fragment used to locate activity and legacy comment destinations in loaded rows. */
  hash: string;
  /** Distinguishes repeated visits to the same hash. */
  visit: number;
}

export interface ActivityNavigation {
  /** True while the requested row should wear its arrival ring. */
  isRinging: boolean;
  request: ActivityNavigationRequest | null;
  scopeId: UUID;
}

interface ScopedNavigation {
  request: ActivityNavigationRequest | null;
  scopeId: UUID;
}

/**
 * Owns activity deep-link navigation for one workflow or task.
 *
 * The returned request is shared by the query and viewport, so fetching an anchored window and
 * placing its row are one operation. The fragment is consumed after being recorded; this lets the
 * browser report a later visit to the same link as a new hash change.
 */
export function useActivityNavigation(scopeId: UUID): ActivityNavigation {
  const navigate = useNavigate();
  const currentHash = useCurrentLocationHash();
  const initialRequest = getInitialRequest(getLocationHash());
  const { pathname, search, state: locationState } = useLocation();
  const [navigation, setNavigation] = useState<ScopedNavigation>(() => ({
    request: initialRequest,
    scopeId,
  }));
  const countedHashRef = useRef(initialRequest?.hash ?? null);
  const [isRinging, setIsRinging] = useState(Boolean(initialRequest));
  const ringExpiryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startRing = useCallback(() => {
    if (ringExpiryRef.current) clearTimeout(ringExpiryRef.current);
    setIsRinging(true);
    ringExpiryRef.current = setTimeout(() => setIsRinging(false), ARRIVAL_HIGHLIGHT_DURATION_MS);
  }, []);

  useEffect(() => {
    if (navigation.scopeId === scopeId) return;
    countedHashRef.current = null;
    setNavigation({ request: null, scopeId });
    setIsRinging(false);
  }, [navigation.scopeId, scopeId]);

  useEffect(() => {
    const hash = parseActivityHash(currentHash);
    if (!hash) {
      countedHashRef.current = null;
      return;
    }

    if (countedHashRef.current !== hash) {
      countedHashRef.current = hash;
      setNavigation(current => ({
        request: {
          activityItemId: parseActivityAnchorId(hash),
          hash,
          visit: ((current.scopeId === scopeId ? current.request?.visit : 0) ?? 0) + 1,
        },
        scopeId,
      }));
    }

    startRing();
    navigate({ hash: '', pathname, search }, { replace: true, state: locationState });
  }, [currentHash, locationState, navigate, pathname, scopeId, search, startRing]);

  return {
    isRinging: navigation.scopeId === scopeId && isRinging,
    request: navigation.scopeId === scopeId ? navigation.request : null,
    scopeId,
  };
}

function getInitialRequest(hash: string): ActivityNavigationRequest | null {
  const parsed = parseActivityHash(hash);
  if (!parsed) return null;

  return { activityItemId: parseActivityAnchorId(parsed), hash: parsed, visit: 1 };
}

function useCurrentLocationHash(): string {
  return useSyncExternalStore(subscribeToLocationHash, getLocationHash, getEmptyHash);
}

function subscribeToLocationHash(onStoreChange: () => void): () => void {
  window.addEventListener('hashchange', onStoreChange);
  window.addEventListener('popstate', onStoreChange);

  return () => {
    window.removeEventListener('hashchange', onStoreChange);
    window.removeEventListener('popstate', onStoreChange);
  };
}

function getLocationHash(): string {
  return typeof window === 'undefined' ? '' : window.location.hash;
}

function getEmptyHash(): string {
  return '';
}

function parseActivityHash(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;

  return decodeUrlHashFragment(raw);
}
